import type { DashboardAssignment, DashboardFieldProgress, DashboardFilters, DashboardInspection } from "./dashboard-indicators.js";
import { computeInspectorPerformance, workedDaysFromRdo, type InspectorPerformanceInput, type InspectorPerformanceReport, type WorkedDayEntry } from "./inspector-performance.js";

/**
 * TRADUÇÃO dos cadastros para a entrada do Painel.
 *
 * Existia só dentro da tela (`overview-page.ts`). O link compartilhado
 * calcula o Painel no SERVIDOR, e duas cópias desta tradução divergiriam
 * com o tempo — o terceiro veria um número diferente do Painel interno.
 * Por isso as duas pontas chamam estas funções.
 */

/** Linha do escopo do Controle de Campo → apontamento do Painel. */
export function toDashboardFieldProgress(
  rows: ReadonlyArray<any> | null | undefined,
  equipmentById?: Map<string, { projectId?: string | null; unitAreaId?: string | null; areaId?: string | null; clientId?: string | null }>
): DashboardFieldProgress[] {
  return (rows ?? []).map((row: any) => {
    const eq = equipmentById?.get(row.lineEquipmentId);
    return {
      lineEquipmentId: row.lineEquipmentId,
      clientId: row.progress?.clientId ?? eq?.clientId ?? null,
      systemId: row.systemId ?? row.progress?.systemId ?? null,
      projectId: row.progress?.projectId ?? eq?.projectId ?? null,
      unitAreaId: row.progress?.unitAreaId ?? eq?.unitAreaId ?? eq?.areaId ?? null,
      progressPercent: row.progress?.progressPercent ?? 0,
      outOfOperation: Boolean(row.progress?.outOfOperation),
      // Base do ritmo diário: carimbada pelo próprio Controle de Campo.
      completedAt: row.progress?.completedAt ?? null,
      startedAt: row.progress?.startedAt ?? null
    };
  });
}

/**
 * `Inspection.inspectorId` é o Professional responsável — nunca
 * `createdBy`, que pode ser quem planejou a inspeção.
 */
export function toDashboardInspections<T extends { inspectorId?: unknown }>(raw: ReadonlyArray<T>): Array<T & DashboardInspection> {
  return raw.map(i => ({ ...i, inspectorProfessionalId: i.inspectorId ? String(i.inspectorId) : null })) as Array<T & DashboardInspection>;
}

export function toDashboardAssignments(raw: ReadonlyArray<any>): DashboardAssignment[] {
  return raw.map((a: any) => {
    const base = {
      inspectorProfessionalId: String(a.inspectorProfessionalId),
      clientId: a.clientId ?? null,
      systemId: a.systemId ? String(a.systemId) : null,
      targetType: a.targetType,
      active: Boolean(a.active),
      projectId: a.projectId ?? null,
      unitAreaId: a.unitAreaId ?? null
    };
    return a.targetId ? { ...base, targetId: String(a.targetId) } : base;
  });
}

/** Só o que o RDO precisa filtrar no servidor; o resto é recorte em memória. */
export function rdoFiltersFor(applied: DashboardFilters): Record<string, string> {
  const filters: Record<string, string> = {};
  if (applied.from) filters["startDate"] = applied.from;
  if (applied.to) filters["endDate"] = applied.to;
  if (applied.projectId) filters["projectId"] = applied.projectId;
  return filters;
}

export interface RankingPerson { id: string; label: string; type?: string }

/**
 * RANKING DE AVANÇO POR INSPETOR.
 *
 * O ranking mantém TODOS no recorte de Projeto / Unidade / Período mesmo
 * com um Inspetor selecionado — a pessoa escolhida é destacada. Filtrar o
 * ranking para uma linha só o esvaziaria de sentido, e o resto do Painel
 * já dá a visão individual.
 *
 * Quem entra na lista: os Profissionais classificados como Inspetor, mais
 * qualquer pessoa que apareça como responsável de inspeção ou com vínculo
 * de campo. Um Engenheiro que executa inspeção não pode ficar invisível
 * por causa da classificação do cadastro.
 */
export function buildInspectorRanking(args: {
  people: readonly RankingPerson[];
  inspections: ReadonlyArray<{ inspectorProfessionalId?: string | null }>;
  assignments: readonly DashboardAssignment[];
  fieldProgress: readonly DashboardFieldProgress[];
  systems: ReadonlyArray<{ id: string; tag: string }> | null | undefined;
  rdoEntries: readonly WorkedDayEntry[];
  applied: DashboardFilters;
  /** REGRA V4-COMPLEMENTO: lineEquipmentId → TAG real da Linha/Tubulação para drill-down. */
  lineLabels?: ReadonlyMap<string, string>;
}): InspectorPerformanceReport {
  const active = new Set<string>([
    ...args.assignments.filter(a => a.active).map(a => a.inspectorProfessionalId),
    ...args.inspections.map(i => String(i.inspectorProfessionalId ?? "")).filter(id => id !== "")
  ]);
  const candidates = args.people.filter(person =>
    person.type === "INSPECTOR" || person.type === "NDT_INSPECTOR" || active.has(person.id));

  const systemLabels = new Map<string, string>();
  for (const system of args.systems ?? []) systemLabels.set(String(system.id), String(system.tag));

  // O recorte do ranking é o do Painel MENOS o Inspetor (ver acima).
  const { inspectorProfessionalId: _selected, ...scope } = args.applied;
  const input: InspectorPerformanceInput = {
    professionals: candidates.map(person => ({ id: person.id, name: person.label })),
    assignments: args.assignments,
    fieldProgress: args.fieldProgress,
    inspections: args.inspections as never,
    systemLabels,
    workedDays: workedDaysFromRdo(args.rdoEntries, scope)
  };
  // REGRA V4-COMPLEMENTO: passar lineLabels apenas quando definido para
  // satisfazer exactOptionalPropertyTypes sem forçar undefined explícito.
  if (args.lineLabels) input.lineLabels = args.lineLabels;
  return computeInspectorPerformance(input, scope);
}
